'use client';

import { useState, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import {
  Loader2,
  Monitor,
  Plus,
  Pencil,
  Printer,
  ScanLine,
  Sparkles,
  Trash2,
  Usb,
  Wifi,
  WifiOff,
  Wrench
} from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { MetricsSkeleton } from '@/components/metrics-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SelectAllBanner } from '@/components/select-all-banner';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { DEVICES_HASH } from '../constants';
import { deviceQueries } from '../services/devices.queries';
import { useDeleteDeviceMutation } from '../services/devices.mutations';
import { DeviceFormDialog } from './device-form-dialog';
import { DevicesTableActionBar } from './devices-table-action-bar';
import { DeviceCard } from './device-card';
import { DeviceCompactRow } from './device-compact-row';
import { DeviceDiscoveryDialog } from '../discovery';
import type {
  Device,
  DevicesListParams,
  DeviceStatus,
  DeviceType,
  DiscoveredDevice
} from '../types';

const STATUS_STYLES: Record<string, string> = {
  online:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  offline: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400',
  maintenance:
    'border-yellow-500/30 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
};

const TYPE_STYLES: Record<DeviceType, string> = {
  scanner:
    'border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-400',
  printer: 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  barcode_scanner:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
};

const TYPE_ICON: Record<
  DeviceType,
  React.ComponentType<{ className?: string }>
> = {
  scanner: ScanLine,
  printer: Printer,
  barcode_scanner: Usb
};

export function DeviceListing() {
  const t = useTranslations('devices');
  // Overlay open-state lives in the URL hash (#create, #edit/<id>, #discover,
  // #delete/<id>) so overlays are deep-linkable and close on browser Back.
  // Destructure the stable callbacks so they can go in hook dep arrays without
  // tripping the React Compiler's manual-memoization check.
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    DEVICES_HASH.create
  );
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(DEVICES_HASH.edit);
  const { open: discoveryOpen, onOpenChange: toggleDiscovery } = useHashDialog(
    DEVICES_HASH.discover
  );
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(DEVICES_HASH.delete);
  // Transient discovered-device data fed into the create form (not a DB row, so
  // it can't ride in the hash) and the resolved match for a discovered device
  // that already exists (may be off the current page, so we keep the object).
  const [prefillDevice, setPrefillDevice] = useState<DiscoveredDevice | null>(
    null
  );
  const [discoveryEditTarget, setDiscoveryEditTarget] = useState<Device | null>(
    null
  );
  const [view, setView] = useDataView('devices');

  const deleteMutation = useDeleteDeviceMutation();
  const queryClient = useQueryClient();

  // URL state
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState('name', parseAsString.withDefault(''));
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlDeviceType] = useQueryState(
    'device_type',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Device>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Device>[])
  );

  const queryParams = useMemo<DevicesListParams>(() => {
    const params: DevicesListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlStatus.length) {
      params.status = urlStatus as DeviceStatus[];
    }
    if (urlDeviceType.length) {
      params.device_type = urlDeviceType as DeviceType[];
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [urlPage, urlPerPage, urlSearch, urlStatus, urlDeviceType, urlSorting]);

  const { data, isPending, isPlaceholderData } = useQuery(
    deviceQueries.list(queryParams)
  );

  const { data: metricsData, isPending: metricsLoading } = useQuery(
    deviceQueries.metrics()
  );

  const devices = useMemo(() => (data?.data ?? []) as Device[], [data]);
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;
  const metrics = metricsData?.data;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  // A discovery match may be off-page, so it falls back to the stored object.
  const editTarget = editOpen
    ? (devices.find((d) => String(d.id) === editId) ??
      (discoveryEditTarget && String(discoveryEditTarget.id) === editId
        ? discoveryEditTarget
        : null))
    : null;
  const deleteTarget = deleteOpen
    ? (devices.find((d) => String(d.id) === deleteId) ?? null)
    : null;

  const columns = useMemo<ColumnDef<Device>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('device')} />
        ),
        cell: ({ row, column }) => (
          <div>
            <span className='font-medium'>
              <HighlightMatch
                text={row.original.name}
                query={getColumnSearchTerms(column)}
              />
            </span>
            {row.original.location && (
              <p className='text-muted-foreground text-xs'>
                {row.original.location}
              </p>
            )}
          </div>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('searchPlaceholder'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: 'devices.column.device'
        }
      },
      {
        id: 'device_type',
        accessorKey: 'device_type',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('type')} />
        ),
        cell: ({ row }) => {
          const type: DeviceType = row.original.device_type ?? 'scanner';
          const Icon = TYPE_ICON[type] ?? ScanLine;
          const label =
            type === 'scanner'
              ? t('deviceTypeBadgeScanner')
              : type === 'printer'
                ? t('deviceTypeBadgePrinter')
                : t('deviceTypeBadgeBarcodeScanner');
          return (
            <Badge
              variant='outline'
              className={cn('gap-1.5', TYPE_STYLES[type])}
            >
              <Icon className='h-3 w-3' />
              {label}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('type'),
          variant: 'multiSelect' as const,
          icon: 'solar:devices-bold-duotone',
          options: [
            { value: 'scanner', label: t('type_scanner') },
            { value: 'printer', label: t('type_printer') },
            { value: 'barcode_scanner', label: t('type_barcode_scanner') }
          ],
          permission: 'devices.column.type'
        }
      },
      {
        id: 'serial_number',
        accessorKey: 'serial_number',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('serialNumber')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground font-mono text-sm'>
            {row.original.serial_number}
          </span>
        ),
        meta: {
          label: t('serialNumber'),
          permission: 'devices.column.serialNumber'
        }
      },
      {
        id: 'ip_address',
        accessorKey: 'ip_address',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('ipAddress')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground font-mono text-sm'>
            {row.original.ip_address}
          </span>
        ),
        meta: { label: t('ipAddress') }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('status')} />
        ),
        cell: ({ row }) => {
          const status = row.original.status;
          const label =
            status === 'online'
              ? t('statusOnline')
              : status === 'offline'
                ? t('statusOffline')
                : t('statusMaintenance');
          return (
            <Badge variant='outline' className={cn(STATUS_STYLES[status])}>
              {label}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('status'),
          variant: 'multiSelect' as const,
          icon: 'solar:tag-bold-duotone',
          options: [
            { value: 'online', label: t('statusOnline') },
            { value: 'offline', label: t('statusOffline') },
            { value: 'maintenance', label: t('statusMaintenance') }
          ],
          permission: 'devices.column.status'
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => {
          const device = row.original;
          return (
            <div className='flex items-center justify-end gap-2'>
              <PermissionGate permissions={['devices.edit']}>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => openEdit(String(device.id))}
                >
                  <Pencil className='h-4 w-4' />
                </Button>
              </PermissionGate>
              <PermissionGate permissions={['devices.delete']}>
                <Button
                  variant='ghost'
                  size='icon'
                  className='text-destructive'
                  onClick={() => openDelete(String(device.id))}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </PermissionGate>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, openEdit, openDelete]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Device>({
    columns: filteredColumns,
    pageKey: 'devices',
    data: devices,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const handleEdit = useCallback(
    (device: Device) => openEdit(String(device.id)),
    [openEdit]
  );

  const handleDelete = useCallback(
    (device: Device) => openDelete(String(device.id)),
    [openDelete]
  );

  async function confirmDelete() {
    if (!deleteTarget) return;
    if ((deleteTarget.workload_assignments_count ?? 0) > 0) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  const isDeleteBlocked =
    !!deleteTarget && (deleteTarget.workload_assignments_count ?? 0) > 0;

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('name'), value: 'name' },
      { label: t('type'), value: 'device_type' },
      { label: t('serialNumber'), value: 'serial_number' },
      { label: t('ipAddress'), value: 'ip_address' },
      { label: t('status'), value: 'status' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  return (
    <div className='space-y-6'>
      {/* Metrics Cards */}
      {metricsLoading ? (
        <MetricsSkeleton />
      ) : metrics ? (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg'>
                <Monitor className='text-primary h-6 w-6' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('totalDevices')}
                </p>
                <p className='text-2xl font-bold'>{metrics.total_devices}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500/10'>
                <Wifi className='h-6 w-6 text-emerald-500' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('onlineDevices')}
                </p>
                <p className='text-2xl font-bold'>{metrics.online_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-gray-500/10'>
                <WifiOff className='h-6 w-6 text-gray-500' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('offlineDevices')}
                </p>
                <p className='text-2xl font-bold'>{metrics.offline_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-500/10'>
                <Wrench className='h-6 w-6 text-yellow-500' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('maintenanceDevices')}
                </p>
                <p className='text-2xl font-bold'>
                  {metrics.maintenance_count}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Toolbar: View Toggle + Add */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='devices'
          />
          <PermissionGate permissions={['devices.create']}>
            <Button variant='outline' onClick={() => toggleDiscovery(true)}>
              <Sparkles className='me-2 h-4 w-4' />
              {t('discoverConnected')}
            </Button>
            <Button
              onClick={() => {
                setPrefillDevice(null);
                setDiscoveryEditTarget(null);
                toggleCreate(true);
              }}
            >
              <Plus className='me-2 h-4 w-4' />
              {t('addDevice')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='devices.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : devices.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Monitor className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noDevices')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {devices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {devices.map((device) => (
              <DeviceCompactRow
                key={device.id}
                device={device}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={devices.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <DevicesTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          ></DataTable>
        </div>
      )}

      {/* Form dialog — #create (optionally prefilled) / #edit/<id> */}
      <DeviceFormDialog
        open={createOpen || (editOpen && !!editTarget)}
        onOpenChange={(open) => {
          if (!open) {
            (editOpen ? closeEdit : toggleCreate)(false);
            setPrefillDevice(null);
            setDiscoveryEditTarget(null);
          }
        }}
        device={editOpen ? editTarget : null}
        prefill={createOpen ? prefillDevice : null}
      />

      {/* Discovery dialog — #discover */}
      <DeviceDiscoveryDialog
        open={discoveryOpen}
        onOpenChange={toggleDiscovery}
        onPick={async (device) => {
          // Look up existing devices of the same type by name to avoid
          // creating duplicates each time the user re-discovers the same
          // hardware. If found, route into the edit flow instead.
          try {
            const existing = await queryClient.fetchQuery(
              deviceQueries.list({
                device_type: [device.device_type],
                per_page: 200
              })
            );
            // Match by the immutable scanner_identifier first so a renamed
            // Device row still gets recognised; fall back to name for legacy
            // rows registered before scanner_identifier existed.
            const identifierTarget = (
              device.scanner_identifier ?? ''
            ).toLowerCase();
            const nameTarget = device.name.toLowerCase();
            const match =
              (identifierTarget &&
                existing.data.find(
                  (d) =>
                    (d.scanner_identifier ?? '').toLowerCase() ===
                    identifierTarget
                )) ||
              existing.data.find(
                (d) =>
                  !d.scanner_identifier && d.name.toLowerCase() === nameTarget
              );
            if (match) {
              setPrefillDevice(null);
              setDiscoveryEditTarget(match);
              toast.info(t('alreadyRegisteredInfo', { name: match.name }));
              openEdit(String(match.id));
            } else {
              setDiscoveryEditTarget(null);
              setPrefillDevice(device);
              toggleCreate(true);
            }
          } catch {
            // If the lookup fails for any reason, fall back to create —
            // the backend's `unique:devices,name` rule will catch real dupes.
            setDiscoveryEditTarget(null);
            setPrefillDevice(device);
            toggleCreate(true);
          }
        }}
      />

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDeleteBlocked
                ? t('deleteBlockedTitle')
                : t('deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDeleteBlocked ? (
                <span className='text-destructive font-medium'>
                  {t('deleteBlockedDescription', {
                    name: deleteTarget?.name ?? '',
                    count: deleteTarget?.workload_assignments_count ?? 0
                  })}
                </span>
              ) : (
                t('deleteConfirmDescription', {
                  name: deleteTarget?.name ?? ''
                })
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            {!isDeleteBlocked && (
              <AlertDialogAction
                onClick={confirmDelete}
                className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
              >
                {deleteMutation.isPending && (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                )}
                {t('delete')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
